import { useMemo, useRef, useState } from 'react';
import './screens.css';
import './LiveLabScreen.css';
import { parseTranscript, applySpeakerMap } from '@/lib/livelab/parseTranscript';
import { analyzeTranscript } from '@/lib/livelab/analyzeTranscript';
import { useLiveLabStore } from '@/state/liveLabStore';
import type {
  CueType,
  LiveLabAnalysis,
  LiveLabSession,
  LiveLabSource,
  LiveLabSpeaker,
  LiveLabTurn,
  ParsedTranscript,
} from '@/types/livelab';

const SAMPLE_TRANSCRIPT = `Sarah: Thanks for hopping on. We've got fifteen minutes — what did you want to walk me through?
Rep: Appreciate the time. Comcast Business is rolling out a new managed network solution for multi-site retailers, and I wanted to see if it might fit what you're doing at MidwestRX.
Sarah: We have an MSP we're pretty happy with. What would make this different?
Rep: Tell me a little about what your MSP handles for you today, and I can speak to where we'd actually move the needle versus where we'd be redundant.
Sarah: They handle our connectivity at all 47 locations and basic firewall management. Honestly the connectivity has been spotty at three or four sites this year.
Rep: That's actually exactly the kind of thing we see a lot. Let me ask — when those sites go down, what does that cost you?
Sarah: Hard to say exactly, but our average store does about eleven thousand a day in scripts. So a four-hour outage at one of the bigger sites — call it a couple thousand dollars. Times the number of sites this happened at this year, it adds up fast.
Rep: We have a really robust SD-WAN solution that should help with that.
Sarah: Right, but my MSP can sell me SD-WAN too. What's different here?
Rep: We have great relationships with our partners and twenty-four-seven support.
Sarah: My CIO has been pushing us to consolidate vendors before fiscal year-end. If I bring this to her, what do I tell her you do that they don't?`;

type AnalysisPhase =
  | { status: 'idle' }
  | { status: 'running'; sessionId: string }
  | { status: 'done'; sessionId: string }
  | { status: 'error'; sessionId: string; message: string };

export default function LiveLabScreen() {
  const sessions = useLiveLabStore((s) => s.sessions);
  const addSession = useLiveLabStore((s) => s.addSession);
  const removeSession = useLiveLabStore((s) => s.removeSession);
  const attachAnalysis = useLiveLabStore((s) => s.attachAnalysis);

  const [raw, setRaw] = useState('');
  const [source, setSource] = useState<LiveLabSource>('paste');
  const [sourceLabel, setSourceLabel] = useState<string | undefined>(undefined);
  const [parsed, setParsed] = useState<ParsedTranscript | null>(null);
  const [speakerMap, setSpeakerMap] = useState<Record<string, LiveLabSpeaker>>({});
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<AnalysisPhase>({ status: 'idle' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fileInputRef = useRef<HTMLInputElement>(null);

  const finalTurns = useMemo(() => {
    if (!parsed) return [];
    return applySpeakerMap(parsed, speakerMap);
  }, [parsed, speakerMap]);

  const repCount = finalTurns.filter((t) => t.speaker === 'rep').length;
  const customerCount = finalTurns.filter((t) => t.speaker === 'customer').length;

  const allSpeakersAssigned =
    !parsed || parsed.auto_classified || parsed.labels.every((l) => speakerMap[l] !== undefined);

  function handleFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      resetWorkspace();
      setRaw(text);
      setSource('upload');
      setSourceLabel(file.name);
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsText(file);
  }

  function resetWorkspace() {
    setParsed(null);
    setSpeakerMap({});
    setError(null);
    setPhase({ status: 'idle' });
  }

  function handleParse() {
    const text = raw.trim();
    if (!text) {
      setError('Paste a transcript or upload a .txt file first.');
      return;
    }
    const result = parseTranscript(text);
    if (result.turns.length === 0) {
      setError(
        'Couldn\'t find any speaker prefixes. Lines should look like "Rep: …" or "Sarah: …".',
      );
      return;
    }
    setParsed(result);
    const initial: Record<string, LiveLabSpeaker> = {};
    for (const turn of result.turns) {
      if (turn.label && initial[turn.label] === undefined) {
        initial[turn.label] = turn.speaker;
      }
    }
    setSpeakerMap(initial);
    setError(null);
    setPhase({ status: 'idle' });
  }

  function handleAssign(label: string, speaker: LiveLabSpeaker) {
    setSpeakerMap((m) => ({ ...m, [label]: speaker }));
  }

  async function handleSaveAndAnalyze() {
    if (!parsed) return;
    if (finalTurns.length === 0) return;
    const session: LiveLabSession = {
      id: `livelab-${Date.now()}`,
      imported_at: new Date().toISOString(),
      source,
      turns: finalTurns,
      raw_text: raw,
      ...(sourceLabel !== undefined && { source_label: sourceLabel }),
    };
    addSession(session);
    setPhase({ status: 'running', sessionId: session.id });
    setExpanded((s) => new Set(s).add(session.id));
    try {
      const analysis = await analyzeTranscript(finalTurns);
      attachAnalysis(session.id, analysis);
      setPhase({ status: 'done', sessionId: session.id });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Analysis failed.';
      setPhase({ status: 'error', sessionId: session.id, message });
    }
  }

  async function handleReanalyze(sessionId: string) {
    const target = sessions.find((s) => s.id === sessionId);
    if (!target) return;
    setPhase({ status: 'running', sessionId });
    setExpanded((s) => new Set(s).add(sessionId));
    try {
      const analysis = await analyzeTranscript(target.turns);
      attachAnalysis(sessionId, analysis);
      setPhase({ status: 'done', sessionId });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Analysis failed.';
      setPhase({ status: 'error', sessionId, message });
    }
  }

  function toggleExpanded(id: string) {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function loadSample() {
    resetWorkspace();
    setRaw(SAMPLE_TRANSCRIPT);
    setSource('paste');
    setSourceLabel(undefined);
  }

  function handleStartNew() {
    setRaw('');
    setSource('paste');
    setSourceLabel(undefined);
    if (fileInputRef.current) fileInputRef.current.value = '';
    resetWorkspace();
  }

  // Workspace is "locked" once the user has saved-and-analyzed; further edits
  // require Start new. Keeps the result visible after the action without
  // confusing the user about whether it's still editable.
  const workspaceLocked =
    phase.status === 'running' || phase.status === 'done' || phase.status === 'error';

  return (
    <div className="screen">
      <div>
        <p className="screen__eyebrow">Live Lab</p>
        <h1 className="screen__title">Practice off your real calls</h1>
        <p className="screen__sub">
          Drop in a real call transcript — pasted or as a <code>.txt</code> file. The system parses
          it into rep/customer turns, runs AI analysis to flag missed cues and wins, and saves the
          result to your library. The re-do role-play step lands next.
        </p>
      </div>

      {/* Step 1 — intake */}
      <section className="livelab__intake">
        <header className="livelab__intake-head">
          <div>
            <p className="livelab__intake-eyebrow">Step 1</p>
            <h2 className="livelab__intake-title">Paste or upload a transcript</h2>
          </div>
          {!workspaceLocked && (
            <label className="livelab__upload">
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,text/plain"
                onChange={handleFileChosen}
              />
              <UploadIcon />
              Upload .txt
            </label>
          )}
        </header>

        {!workspaceLocked && (
          <textarea
            className="livelab__textarea"
            placeholder={
              'Paste your transcript here. Lines should be prefixed with a speaker, e.g.:\n\nRep: Appreciate the time today.\nCustomer: We\'ve only got fifteen minutes.\n\nNamed speakers like "Sarah:" and "Mike:" also work — you\'ll just be asked which one is the rep.'
            }
            value={raw}
            onChange={(e) => {
              resetWorkspace();
              setRaw(e.target.value);
              setSource('paste');
              setSourceLabel(undefined);
            }}
          />
        )}

        {error && <p className="livelab__error">{error}</p>}

        {!workspaceLocked && (
          <div className="livelab__actions">
            <button
              type="button"
              className="livelab__btn livelab__btn--primary"
              onClick={handleParse}
              disabled={!raw.trim()}
            >
              Parse transcript
            </button>
            <button type="button" className="livelab__btn livelab__btn--ghost" onClick={loadSample}>
              Load sample
            </button>
            <p className="livelab__hint">
              Recognized prefixes: <code>Rep:</code>, <code>Customer:</code>, <code>Sales:</code>,{' '}
              <code>Client:</code>, <code>AE:</code>, <code>Prospect:</code>, plus any first name.
              Timestamps like <code>[00:01:23]</code> are kept.
            </p>
          </div>
        )}

        {workspaceLocked && (
          <div className="livelab__actions">
            <button
              type="button"
              className="livelab__btn livelab__btn--ghost"
              onClick={handleStartNew}
            >
              Start new transcript
            </button>
          </div>
        )}
      </section>

      {/* Speaker mapping prompt */}
      {parsed && !parsed.auto_classified && !workspaceLocked && (
        <div className="livelab__speakers">
          <p className="livelab__speakers-title">
            Who's who? Pick the rep vs. the customer for each name found.
          </p>
          {parsed.labels.map((label) => (
            <div key={label} className="livelab__speakers-row">
              <span className="livelab__speakers-name">{label}</span>
              <div className="livelab__speakers-choices" role="group">
                <button
                  type="button"
                  className="livelab__speakers-choice"
                  aria-pressed={speakerMap[label] === 'rep'}
                  onClick={() => handleAssign(label, 'rep')}
                >
                  Rep
                </button>
                <button
                  type="button"
                  className="livelab__speakers-choice"
                  aria-pressed={speakerMap[label] === 'customer'}
                  onClick={() => handleAssign(label, 'customer')}
                >
                  Customer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Parsed-transcript preview (only shown pre-save) */}
      {parsed && finalTurns.length > 0 && !workspaceLocked && (
        <section className="livelab__preview">
          <header className="livelab__preview-head">
            <div>
              <p className="livelab__intake-eyebrow">Parsed</p>
              <h2 className="livelab__intake-title">Transcript preview</h2>
            </div>
            <p className="livelab__preview-stats">
              {finalTurns.length} turns · <strong>{repCount}</strong> rep ·{' '}
              <strong>{customerCount}</strong> customer
            </p>
          </header>

          <ul className="livelab__preview-list">
            {finalTurns.map((t, i) => (
              <li key={i} className={`livelab__turn livelab__turn--${t.speaker}`}>
                <span className="livelab__turn-who">
                  {t.label ?? (t.speaker === 'rep' ? 'Rep' : 'Customer')}
                  {t.at_s !== undefined && (
                    <>
                      {' · '}
                      <span className="livelab__turn-time">{formatTime(t.at_s)}</span>
                    </>
                  )}
                </span>
                <p className="livelab__turn-text">{t.text}</p>
              </li>
            ))}
          </ul>

          <div className="livelab__actions">
            <button
              type="button"
              className="livelab__btn livelab__btn--primary"
              onClick={handleSaveAndAnalyze}
              disabled={!allSpeakersAssigned}
            >
              Run AI analysis →
            </button>
            {!allSpeakersAssigned && (
              <p className="livelab__hint">
                Assign every name to Rep or Customer above to enable analysis.
              </p>
            )}
          </div>
        </section>
      )}

      {/* Saved sessions library */}
      {sessions.length > 0 && (
        <section className="livelab__saved">
          <p className="livelab__saved-head">Your library</p>
          {sessions.map((s) => {
            const isExpanded = expanded.has(s.id);
            const isRunning = phase.status === 'running' && phase.sessionId === s.id;
            const errMsg =
              phase.status === 'error' && phase.sessionId === s.id ? phase.message : null;
            return (
              <article key={s.id} className="livelab__saved-card">
                <div className="livelab__saved-row">
                  <div className="livelab__saved-meta">
                    <p className="livelab__saved-label">
                      {s.source_label ?? `Pasted transcript · ${formatDate(s.imported_at)}`}
                    </p>
                    <p className="livelab__saved-sub">
                      {s.turns.length} turns · {s.turns.filter((t) => t.speaker === 'rep').length}{' '}
                      rep · {s.turns.filter((t) => t.speaker === 'customer').length} customer
                      {' · '}
                      {s.analysis ? (
                        <strong>
                          {s.analysis.missed_cues.length} missed · {s.analysis.wins.length} wins
                        </strong>
                      ) : isRunning ? (
                        <em>analyzing…</em>
                      ) : (
                        <em>not analyzed</em>
                      )}
                    </p>
                  </div>
                  <div className="livelab__saved-actions">
                    {!s.analysis && !isRunning && (
                      <button
                        type="button"
                        className="livelab__btn livelab__btn--ghost"
                        onClick={() => handleReanalyze(s.id)}
                      >
                        Analyze
                      </button>
                    )}
                    {(s.analysis || isRunning || errMsg) && (
                      <button
                        type="button"
                        className="livelab__btn livelab__btn--ghost"
                        onClick={() => toggleExpanded(s.id)}
                      >
                        {isExpanded ? 'Hide' : 'Show'} analysis
                      </button>
                    )}
                    <button
                      type="button"
                      className="livelab__btn livelab__btn--ghost"
                      onClick={() => removeSession(s.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="livelab__saved-body">
                    {isRunning && <AnalysisRunning />}
                    {errMsg && (
                      <p className="livelab__error">
                        {errMsg}{' '}
                        <button
                          type="button"
                          className="livelab__btn livelab__btn--ghost"
                          onClick={() => handleReanalyze(s.id)}
                        >
                          Retry
                        </button>
                      </p>
                    )}
                    {s.analysis && <AnalysisPanel analysis={s.analysis} turns={s.turns} />}
                  </div>
                )}
              </article>
            );
          })}
        </section>
      )}

      {/* What comes next */}
      <section className="livelab__next">
        <article className="livelab__next-card">
          <p className="livelab__next-eyebrow">Next · Step 3</p>
          <h3 className="livelab__next-title">Re-do role-play</h3>
          <p className="livelab__next-desc">
            One-click into a generated scenario that recreates the customer's posture and the missed
            moments — at-bat you didn't get the first time.
          </p>
        </article>
      </section>
    </div>
  );
}

/* ------------------------------ Analysis UI ----------------------------- */

function AnalysisRunning() {
  return (
    <div className="livelab__analysis-running">
      <span className="livelab__spinner" aria-hidden />
      Analyzing transcript…
    </div>
  );
}

interface AnalysisPanelProps {
  analysis: LiveLabAnalysis;
  turns: LiveLabTurn[];
}

function AnalysisPanel({ analysis, turns }: AnalysisPanelProps) {
  return (
    <div className="livelab__analysis">
      <header className="livelab__analysis-head">
        <p className="livelab__intake-eyebrow">Analysis</p>
        <span className={`livelab__posture livelab__posture--${analysis.posture}`}>
          {analysis.posture}
        </span>
      </header>
      <p className="livelab__analysis-summary">{analysis.summary}</p>
      <p className="livelab__analysis-evidence">
        <em>Posture read:</em> {analysis.posture_evidence}
      </p>

      {analysis.missed_cues.length > 0 && (
        <div className="livelab__cues">
          <h4 className="livelab__cues-head">Missed cues ({analysis.missed_cues.length})</h4>
          <ul className="livelab__cues-list">
            {analysis.missed_cues.map((cue, i) => (
              <li key={i} className="livelab__cue">
                <div className="livelab__cue-tag">{cueTypeLabel(cue.cue_type)}</div>
                <p className="livelab__cue-quote">
                  <strong>{turns[cue.at_turn_index]?.label ?? 'Customer'}:</strong> "
                  {cue.customer_quote}"
                </p>
                <p className="livelab__cue-response">
                  <strong>You:</strong> "{cue.rep_response}"
                </p>
                <p className="livelab__cue-why">{cue.why_missed}</p>
                <p className="livelab__cue-try">{cue.what_to_try}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {analysis.wins.length > 0 && (
        <div className="livelab__wins">
          <h4 className="livelab__wins-head">Wins ({analysis.wins.length})</h4>
          <ul className="livelab__wins-list">
            {analysis.wins.map((win, i) => (
              <li key={i} className="livelab__win">
                <p className="livelab__win-quote">"{win.rep_quote}"</p>
                <p className="livelab__win-why">{win.why_strong}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {analysis.missed_cues.length === 0 && analysis.wins.length === 0 && (
        <p className="livelab__hint">
          No specific cues or wins surfaced — try a longer transcript with more substantive customer
          turns.
        </p>
      )}

      <p className="livelab__analysis-source">
        Generated {formatDate(analysis.generated_at)} · source: {analysis.source}
      </p>
    </div>
  );
}

function cueTypeLabel(t: CueType): string {
  switch (t) {
    case 'pain_hinted':
      return 'Pain hinted';
    case 'budget_signal':
      return 'Budget signal';
    case 'timeline_signal':
      return 'Timeline signal';
    case 'champion_signal':
      return 'Champion signal';
    case 'objection':
      return 'Objection';
    case 'buying_question':
      return 'Buying question';
  }
}

/* ------------------------------ Helpers --------------------------------- */

function formatTime(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const SVG_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function UploadIcon() {
  return (
    <svg width="14" height="14" {...SVG_PROPS}>
      <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
      <path d="M12 4v12" />
      <path d="m7 9 5-5 5 5" />
    </svg>
  );
}
